import { buildCompanionFreshness } from "./freshness";

export type CompanionTrustConfidence = "high" | "medium" | "low";
export type CompanionTrustScoreKind = "generic-fallback" | "sport-specific";

export type CompanionCrowdReport = {
  reportedAt: string | null;
  agrees: boolean;
};

export type CompanionGroundTruthAgreement = {
  agrees: number;
  wrongs: number;
};

export type CompanionTrustState = {
  freshnessLabel: string;
  confidence: CompanionTrustConfidence;
  badge: string;
  sourceCount: number;
};

export type CompanionTrustInput = {
  score: number | null;
  scoreKind: CompanionTrustScoreKind;
  updatedAt: string | null;
  sourceCount?: number;
  crowdReports?: CompanionCrowdReport[];
  groundTruth?: CompanionGroundTruthAgreement | null;
  now?: Date;
};

const CROWD_REPORT_FRESH_HOURS = 12;
const CROWD_AGREEMENT_MINIMUM = 2;

export function buildCompanionTrustState(input: CompanionTrustInput): CompanionTrustState {
  const now = input.now ?? inferTrustNow(input);
  const freshness = buildCompanionFreshness(input.updatedAt, now);
  const crowdAgreeCount = freshCrowdAgreeCount(input.crowdReports ?? [], now);
  const groundTruthAgreeCount = qualifyingGroundTruthAgreement(input.groundTruth)
    ? Math.max(0, Math.trunc(input.groundTruth?.agrees ?? 0))
    : 0;
  const baseSourceCount = Math.max(0, Math.trunc(input.sourceCount ?? 0));
  const sourceCount = baseSourceCount + crowdAgreeCount + groundTruthAgreeCount;

  if (input.score === null || freshness.state === "missing") {
    return {
      freshnessLabel: "No fresh score",
      confidence: "low",
      badge: "Low data",
      sourceCount,
    };
  }

  if (freshness.state === "stale") {
    return {
      freshnessLabel: freshness.label,
      confidence: "low",
      badge: "Low data",
      sourceCount,
    };
  }

  if (crowdAgreeCount >= CROWD_AGREEMENT_MINIMUM || groundTruthAgreeCount >= CROWD_AGREEMENT_MINIMUM) {
    return {
      freshnessLabel: freshness.label,
      confidence: "high",
      badge: "People on the ground agree",
      sourceCount,
    };
  }

  if (input.scoreKind === "sport-specific" && sourceCount >= 2) {
    return {
      freshnessLabel: freshness.label,
      confidence: "high",
      badge: "High confidence",
      sourceCount,
    };
  }

  return {
    freshnessLabel: freshness.label,
    confidence: "medium",
    badge: "Medium confidence",
    sourceCount,
  };
}

function qualifyingGroundTruthAgreement(
  agreement: CompanionGroundTruthAgreement | null | undefined,
): boolean {
  if (!agreement) return false;
  return agreement.agrees >= CROWD_AGREEMENT_MINIMUM && agreement.wrongs === 0;
}

function freshCrowdAgreeCount(reports: CompanionCrowdReport[], now: Date): number {
  return reports.filter((report) => {
    if (!report.agrees || !report.reportedAt) return false;
    const reportedTime = Date.parse(report.reportedAt);
    if (!Number.isFinite(reportedTime)) return false;
    const ageHours = (now.getTime() - reportedTime) / (60 * 60 * 1000);
    return ageHours >= 0 && ageHours <= CROWD_REPORT_FRESH_HOURS;
  }).length;
}

function inferTrustNow(input: Pick<CompanionTrustInput, "updatedAt" | "crowdReports">): Date {
  const timestamps = [
    input.updatedAt,
    ...(input.crowdReports ?? []).map((report) => report.reportedAt),
  ]
    .map((value) => (value ? Date.parse(value) : NaN))
    .filter(Number.isFinite);

  if (timestamps.length === 0) return new Date(0);
  return new Date(Math.max(...timestamps));
}
